import { defineComponent, reactive, ref, onUnmounted, watch, PropType } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import Tracker from '@nio-fe/nio-tracker-web'
import { gcj02ToBd09 } from '@/utils/coordinateTransform'
import debounce from 'lodash.debounce'
import { setMapStyleByConfig } from './biz/utils'
import { OS } from '@/utils/device'
import ChargerMap from '../../components/chargerMap'
import { ChargeMapContext } from '../../components/chargerMap/index.model'
import {
  flyTo,
  fitWindows,
  changeChargingStatus,
  calcSize,
} from '../../components/chargerMap/mapUtils'
import WsService from '../../components/wsService'
import CityBoundary from '../../components/cityBoundary'
import XinfaDotAndLine from '../../components/xinfaDotAndLine'
import AutoRefreshXinfa from '../../components/autoRefresh/autoRefreshXinfa'
import { useAppStore } from '../../stores/app'
import { useComponentStore } from '../localStores/component'
import { FilterItem, FilterState } from '../../components/filter/filter.model'
import { Point, CityInfo, MapViewMode, ZoomMode } from '../../contract/map'
import { RouteCDNModel } from '../../models/detail.model'
import {
  DEFAULT_CITY_ZOOM_LEVEL,
  DEFAULT_COUNTRY_ZOOM_LEVEL,
  DEFAULT_DISTRICT_ZOOM_LEVEL,
} from '../../config/zoomLevelConfig'
import specialArea from '../../config/specialArea.json'
import { getChargemapCDNurl } from '../../apis/map.api'
import { DEFAULT_POSITION } from '../../config/defaultPosition'
import Filter from '../../components/filter'
// import Information from '../../components/informationComps/information'
import InformationV2 from '../../components/informationComps/informationV2'
import XinfaIconRender from '../../components/iconRender/xinfaIcon'
export default defineComponent({
  name: 'city-map-view',
  props: {
    mapContext: {
      type: Object as PropType<ChargeMapContext>,
    },
  },
  setup: () => {
    let mapContext: ChargeMapContext
    const mapContextRef = ref<ChargeMapContext>()
    const mapInstance = ref<any>()
    const BMapGL = ref<any>()

    const router = useRouter()
    const route = useRoute()
    const appStore = useAppStore()
    const componentStore = useComponentStore()

    let selectedCityLoc: Point | null

    // 获取组件配置
    const config: any = window.NSComponentsConfig

    const state = reactive({
      showSummary: false,
      showHandlebox: false,
      mapSetting: {
        center: appStore.center, // 初始化中心点
        zoom: appStore.zoomLevel, // 初始化zoom
        minZoom: appStore.minZoomLevel,
        maxZoom: appStore.maxZoomLevel,
        infoZoomLevel: 12, // 合适看信息的zoom eg. 搜索，切换数据
      },
      zoomMode: ZoomMode.Country,
    })
    let thirdSourceIndex: number[] = [] // 三方点的位置
    let resourceFilter: FilterItem[] = [] // 资源点

    const cdnUrlResProps = ref<RouteCDNModel>()

    // click隐藏logo和数据概览面板
    const showHandlebox = () => {
      state.showHandlebox = true
    }
    const hideHandlebox = () => {
      state.showHandlebox = false
    }
    const showSummary = () => {
      state.showSummary = true
      if (!OS.isNioBrowser) {
        // nio浏览器不可点击
        document.addEventListener('touchend', hideSummary)
        document.addEventListener('click', hideSummary)
      }
      hideHandlebox()
    }
    const hideSummary = () => {
      if (!state.showSummary) return
      state.showSummary = false
      document.removeEventListener('touchend', hideSummary)
      document.removeEventListener('click', hideSummary)
      showHandlebox()
    }
    componentStore.bus.on('hideSummary', () => {
      hideSummary()
    })

    /**
     * filter的初始化，用于判断是否接收ws的实时换电数据
     */
    componentStore.bus.on('initResourceFilter', (params: any) => {
      thirdSourceIndex = params.thirdSourceIndex
      resourceFilter =
        params.filter[thirdSourceIndex[0]]?.options[thirdSourceIndex[1]]?.options || []
    })

    // 选择城市
    componentStore.bus.on('selectCity', (info: CityInfo) => {
      componentStore.$patch({
        selectedCityFromAddressPod: JSON.parse(JSON.stringify(info)) as CityInfo,
      })
      selectedCityLoc = info.loc // 位置都以gis给的文件为准
      if (info.code !== '000000') {
        if (info.useDefaultZoom) {
          // 需要单独处理的地区
          flyTo(mapContext, gcj02ToBd09(selectedCityLoc), info.zoom)
        } else {
          const infoName = info.simple!.split('_')[0].split('-').join('')
          // 使用gis给的location 用百度的会有误差
          mapContext
            .getAreaBoundary(infoName)
            .then((result: any) => {
              let resZoom = result.zoom
              switch (componentStore.dimension) {
                case '省':
                  resZoom =
                    result.zoom > DEFAULT_CITY_ZOOM_LEVEL ? DEFAULT_CITY_ZOOM_LEVEL : result.zoom
                  break
                case '市':
                  resZoom =
                    result.zoom > DEFAULT_DISTRICT_ZOOM_LEVEL
                      ? DEFAULT_DISTRICT_ZOOM_LEVEL
                      : result.zoom
                  break
                case '区县':
                  resZoom =
                    result.zoom > DEFAULT_DISTRICT_ZOOM_LEVEL
                      ? result.zoom
                      : DEFAULT_DISTRICT_ZOOM_LEVEL
                  break

                default:
                  break
              }
              if (info.useGeometricCenter) {
                // 需要单独处理的地区
                flyTo(mapContext, result.center, info.zoom)
              } else {
                flyTo(mapContext, gcj02ToBd09(selectedCityLoc!), resZoom)
              }
            })
            .catch(() => {
              console.log(
                '============// 有些信息查不到（eg.直辖县）走默认zoom========================'
              )
              // 有些信息查不到（eg.直辖县）走默认zoom
              flyTo(mapContext, gcj02ToBd09(selectedCityLoc!), info.zoom)
            })
        }
      } else {
        flyTo(mapContext, gcj02ToBd09(selectedCityLoc!), state.mapSetting.zoom)
      }
    })

    /**
     * 更新位置
     * @param loc 设置位置中心
     * @param refreshContent 更新中心位置是否需要刷新地图内容 默认true
     */
    const updateCenterLocation = async (loc: Point) => {
      if (window.updateCenterLocationDebounce) {
        window.updateCenterLocationDebounce.cancel()
        delete window.updateCenterLocationDebounce
      }
      window.updateCenterLocationDebounce = debounce(
        async () => {
          await componentStore.setCenter(loc)
          selectedCityLoc = null
          if (componentStore.centerLocation) {
            if (specialArea.indexOf(componentStore.centerLocation.province) > -1) {
              await componentStore.selectCityByArr([componentStore.centerLocation.province])
            } else if (componentStore.dimension) {
              if (componentStore.dimension == '区县') {
                await componentStore.selectCityByArr([
                  componentStore.centerLocation.province,
                  componentStore.centerLocation.city,
                  componentStore.centerLocation.district,
                ])
              } else {
                await componentStore.selectCityByArr([
                  componentStore.centerLocation.province,
                  componentStore.centerLocation.city,
                ])
              }
              componentStore.$patch({ dimension: '' })
            } else {
              const zoomLevel = mapContext.getZoom()
              if (zoomLevel > DEFAULT_DISTRICT_ZOOM_LEVEL) {
                await componentStore.selectCityByArr([
                  componentStore.centerLocation.province,
                  componentStore.centerLocation.city,
                  componentStore.centerLocation.district,
                ])
              } else {
                await componentStore.selectCityByArr([
                  componentStore.centerLocation.province,
                  componentStore.centerLocation.city,
                ])
              }
            }
          }
        },
        800,
        { leading: false }
      )
      window.updateCenterLocationDebounce()
    }

    const renderResourceOnMapReady = () => {
      getChargemapCDNurl(componentStore.dictionaries).then((res: any) => {
        cdnUrlResProps.value = res
      })
    }

    // -------------------------- 事件 -----------------------
    const onMapReady = async (context: ChargeMapContext) => {
      mapContext = context
      mapContextRef.value = context
      mapInstance.value = mapContextRef.value?.getMap()
      BMapGL.value = window.BMapGL
      if (state.showSummary) {
        disableZoom()
        disableDragging()
      }
      // 自适应屏幕 将中国定位到地图中心
      const initCenterZoomInit = fitWindows(mapContext, [
        appStore.chinaBoundSW,
        appStore.chinaBoundNE,
      ])
      setMapStyleByConfig(mapContext, config) // 读取配置样式
      // 请求cdn资源
      setTimeout(() => {
        // 门店信发旧设备会渲染卡死，需要手动读取delay
        renderResourceOnMapReady()
      }, config?.delay_render?.properties?.delay || 5000)
      const initCenterZoom = OS.isNioBrowser
        ? {
            center: mapContext.getCenter(),
            zoom: mapContext.getZoom(),
          }
        : initCenterZoomInit
      state.mapSetting.center = initCenterZoom.center
      state.mapSetting.zoom = initCenterZoom.zoom
      state.mapSetting.minZoom = initCenterZoom.zoom
      mapContext.setZoom(initCenterZoom.zoom)
      mapContext.setMinZoom(initCenterZoom.zoom) // 设置地图允许的最小级别
      mapContext.setMaxZoom(state.mapSetting.maxZoom)
      mapContext.getMap().setMapType((window as any).BMAP_NORMAL_MAP)
      mapContext.showMapText(false) // 默认不显示文字
      componentStore.$patch({ mapContext: context }) // 记录地图 给其他组件使用
      // 埋点：loading关闭时间
      const loadingEndTime = new Date().getTime()
      appStore.pageStartTime &&
        Tracker.track(
          'nsh5_loadingclose',
          Object.assign(appStore.trackParamsCommon, {
            timebetween: loadingEndTime - appStore.pageStartTime,
          })
        )
      context
        .getCurrentPosition(config?.location?.properties?.option || {})
        .then(async (loc: Point) => {
          componentStore.setMyLocation(loc)
          componentStore.bus.emit('checkCanPreviewCity', loc)
        })
        .catch(async () => {
          componentStore.setMyLocation(DEFAULT_POSITION)
          componentStore.bus.emit('checkCanPreviewCity', null)
          appStore.$patch({
            locationSuccess: false,
          })
        })
    }

    const disableZoom = () => {
      const mapIns = mapContext?.getMap()
      mapIns?.disablePinchToZoom() // 禁用双指缩放地图
      mapIns?.disableScrollWheelZoom() // 禁止地图被鼠标滚轮缩放
    }

    const enableZoom = () => {
      const mapIns = mapContext?.getMap()
      mapIns?.enablePinchToZoom() // 禁用双指缩放地图
      mapIns?.enableScrollWheelZoom() // 禁止地图被鼠标滚轮缩放
    }

    const disableDragging = () => {
      const mapIns = mapContext?.getMap()
      mapIns?.disableDragging()
    }

    const enableDragging = () => {
      const mapIns = mapContext?.getMap()
      mapIns?.enableDragging()
    }

    // 点击路线的时候打断首页的bling
    componentStore.bus.on('handleBlingClear', () => {
      if (state.zoomMode != ZoomMode.City) {
        mapContext.interruptBling()
      }
    })

    const sendRealTimeNum = (obj: any) => {
      // 实时换电
      if (Object.prototype.hasOwnProperty.call(obj, 'realtime_swap_order_num')) {
        if (config?.data_default?.properties?.stop_flip) {
          // 取消数字跳动
          return
        }
        if (obj['realtime_swap_order_num'] && obj['realtime_swap_order_num'].num > 0) {
          // 换电数据变化
          // console.log('===============全国---换电数据变化=====================')
          // console.log(obj['realtime_swap_order_num'])
          // console.log('====================================')
          componentStore.$patch({ swapNum: obj['realtime_swap_order_num'].num })
        }
      }
      // 实时充电
      if (Object.prototype.hasOwnProperty.call(obj, 'realtime_charge_order_num')) {
        if (config?.data_default?.properties?.stop_flip) {
          // 取消数字跳动
          return
        }
        if (obj['realtime_charge_order_num'] && obj['realtime_charge_order_num'].num > 0) {
          // 充电数据变化
          // console.log('===============全国---充电数据变化=====================')
          // console.log(obj['realtime_charge_order_num'])
          // console.log('====================================')
          componentStore.$patch({ chargeNum: obj['realtime_charge_order_num'].num })
        }
      }
      // 实时累计换电数量或实时换电站数量达成，触发运营位id
      if (Object.prototype.hasOwnProperty.call(obj, 'hit_act_id')) {
        if (obj['hit_act_id']) {
          // 触发运营位id
          // console.log('===============触发运营位id=====================')
          // console.log(obj['hit_act_id'])
          // console.log('====================================')
          componentStore.bus.emit('screenPopupShow', obj['hit_act_id'])
          // 触发数字动画背景
          setTimeout(() => {
            componentStore.bus.emit('numberAnimationShow', obj['hit_act_id'])
          }, config?.number_animation?.properties?.delay_time || 1500);
        }
      }
      // 换电完成bling效果
      if (config?.finished_swap_status?.show) {
        if (Object.prototype.hasOwnProperty.call(obj, 'finished_swap_location')) {
          if (
            state.showSummary ||
            (componentStore.viewMode == MapViewMode.Init && // 地图模式，心愿站和路径规划不监听
              state.zoomMode != ZoomMode.City && // 全国模式，路线和城市不监听
              obj['finished_swap_location'] &&
              route.name == 'home' && // 只有首页展示
              (resourceFilter[0]?.filterState == FilterState.On ||
                (resourceFilter[0]?.filterState == FilterState.Off &&
                  resourceFilter[1]?.filterState == FilterState.Off &&
                  resourceFilter[2]?.filterState != FilterState.On))) // 筛选  有换电站情况下监听
          ) {
            // 换电完成Bling
            const locations = obj['finished_swap_location']
            const blingPoints = locations.map((location: string) => {
              const position = location.split(',') // gcj
              const point = gcj02ToBd09({
                lng: Number(position[0]),
                lat: Number(position[1]),
              })
              return point
            })
            mapContext?.addBlingAnimation(blingPoints, calcSize(mapContext.getZoom()).ratio)
          }
        }
      }
    }

    const onZoomEnd = async () => {
      const zoomLevel = mapContext.getZoom()
      state.zoomMode =
        zoomLevel > DEFAULT_CITY_ZOOM_LEVEL
          ? ZoomMode.City
          : zoomLevel > DEFAULT_COUNTRY_ZOOM_LEVEL
          ? ZoomMode.Route
          : ZoomMode.Country
      mapContext.getMap().setDisplayOptions({
        poiText: state.zoomMode != ZoomMode.Country,
        poiIcon: state.zoomMode != ZoomMode.Country,
        street: state.zoomMode != ZoomMode.Country,
      })
      // 缩放到最小回到中心
      if (zoomLevel - 0.2 <= state.mapSetting.minZoom) {
        flyTo(mapContext, state.mapSetting.center)
      }
      updateCenterLocationInfo()
      componentStore.$patch({
        showPsDistrictHousingCoveredRate: zoomLevel <= DEFAULT_DISTRICT_ZOOM_LEVEL,
      })
    }

    const onZoomStart = () => {
      mapContext.clearAllPointLayer()
      // 20250605 因为大屏小点消失层级变深，所以信发上不需要处理大点
      mapContext.operatePolylineLayer('highway', false)
      // mapContext.hideMapVglMarkers()
    }

    /**
     * 根据屏幕中心位置结合zoomLevel获取中心点信息
     */
    const updateCenterLocationInfo = async () => {
      // 按条件更新位置
      switch (state.zoomMode) {
        case ZoomMode.Country:
          componentStore.$patch({ dimension: '' }) // 回到全国需要清空数据看板的选择
          selectedCityLoc = null
          componentStore.selectCityByArr(['全国'])
          break
        case ZoomMode.Route:
          await componentStore.setCenter(selectedCityLoc || mapContext.getCenter())
          selectedCityLoc = null
          componentStore.centerLocation &&
            componentStore.selectCityByArr([componentStore.centerLocation.province])
          componentStore.$patch({ dimension: '' }) // 修复选择选择省后再拖动无反应
          break
        case ZoomMode.City:
          updateCenterLocation(selectedCityLoc || mapContext.getCenter())
          break

        default:
          break
      }
    }

    watch(
      () => state.showSummary,
      () => {
        if (state.showSummary) {
          disableZoom()
          disableDragging()
        } else {
          enableZoom()
          enableDragging()
        }
      }
    )

    onUnmounted(() => {
      componentStore.bus.off('*')
    })

    // ********************************************

    router.afterEach(async (to, from) => {
      if (!from.name && to.name === 'home') {
        showSummary() // 入口就是首页，显示summary
        return
      }
      if (to.name === 'home') {
        componentStore.$patch({ viewMode: MapViewMode.Init })
        state.showHandlebox = true
        return
      }
      if (to.name === 'data_summary' || to.name === 'data_chooseCity') {
        componentStore.$patch({ viewMode: MapViewMode.Init })
        state.showHandlebox = false
        return
      }
    })

    return () => {
      return (
        <>
          {mapContextRef.value && (
            <>
              <XinfaDotAndLine
                mapContextRef={mapContextRef.value}
                mapInstance={mapInstance.value}
                componentStore={componentStore}
                cdnUrl={cdnUrlResProps.value}
              ></XinfaDotAndLine>
              <CityBoundary
                mapContextRef={mapContextRef.value}
                mapInstance={mapInstance.value}
              ></CityBoundary>
              {/* 20250605 因为大屏小点消失层级变深，所以信发上不需要处理大点 */}
              {/* <XinfaIconRender
                mapContextRef={mapContextRef.value}
                mapInstance={mapInstance.value}
                componentStore={componentStore}
              ></XinfaIconRender> */}
              {state.showSummary && <InformationV2 />}
              <AutoRefreshXinfa
                mapContextRef={mapContextRef.value}
                componentStore={componentStore}
                mapSetting={state.mapSetting}
                onBackHome={showSummary}
              ></AutoRefreshXinfa>
            </>
          )}
          <ChargerMap
            settings={state.mapSetting}
            onReady={onMapReady}
            onZoomend={onZoomEnd}
            onZoomstart={onZoomStart}
          />
          <WsService onReceiveChargeMapMessage={sendRealTimeNum}></WsService>
        </>
      )
    }
  },
})
